import * as Sentry from '@sentry/react'
import useSWR, { SWRConfiguration } from 'swr'

import { destroyAuthSession, useAuth } from '../../context/auth'

const API_URL = import.meta.env.VITE_API_URL

export type ErrorType = {
  code: string
  message: string
}

export const handleUnauthorized = (status: number) => {
  if (status === 401) {
    const hadSession = !!sessionStorage.getItem('authState')
    destroyAuthSession()
    if (hadSession) {
      window.location.href = '/session-expired'
    }
  }
}

// Build an Error from a non-OK response. API route handlers always return a structured
// { code, message } body, so anything missing that shape came from outside the app
// (API Gateway 5xx/timeout, or Express middleware returning an HTML error page). Capture
// the HTTP details for those so they stop surfacing as a blind "unknown error" — we can
// then tell a 504 from a 413 from a crash instead of guessing.
export const errorFromResponse = async (res: Response, method: string): Promise<Error & { code: string }> => {
  const bodyText = await res.text().catch(() => '')

  let errorData: { code?: string; message?: string } | undefined
  try {
    errorData = JSON.parse(bodyText)
  } catch {
    // Response wasn't JSON (e.g. an HTML error page or gateway timeout body)
  }

  if (errorData?.code && errorData?.message) {
    const e = new Error(errorData.message) as Error & { code: string }
    e.code = errorData.code
    return e
  }

  const e = new Error(`Request failed (${res.status} ${res.statusText})`.trim()) as Error & { code: string }
  e.code = `HTTP_${res.status}`
  // A 401 is an expected auth outcome (handled by handleUnauthorized — expired session
  // or an unauthenticated page firing a query); don't report it to Sentry as an error.
  // Capture the genuinely unexpected non-structured errors (5xx, gateway timeouts, HTML
  // error pages) that this helper exists to diagnose.
  if (res.status !== 401) {
    Sentry.captureException(e, {
      tags: { non_structured_error: true, http_status: res.status },
      extra: {
        method,
        url: res.url,
        status: res.status,
        statusText: res.statusText,
        contentType: res.headers.get('content-type'),
        bodySnippet: bodyText.slice(0, 500),
      },
    })
  }
  return e
}

export const fetcher = async <T = unknown>({ url, token }: { url: string; token?: string }): Promise<T> => {
  const res = await fetch(url, {
    headers: { UQRDM_TOKEN: token || '', 'Content-Type': 'application/json' },
  })

  if (!res.ok) {
    handleUnauthorized(res.status)
    throw await errorFromResponse(res, 'GET')
  }

  return (await res.json()) as T
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type QueryOptions<T extends Record<string, string> = {}> = SWRConfiguration & {
  params?: T
  skip?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export function useQuery<ResponseData, QueryParams extends Record<string, string> = {}>(
  path: string,
  options: QueryOptions<QueryParams> = {},
) {
  const { params, skip = false, ...swrOptions } = options
  const swrConfig: SWRConfiguration = {
    refreshInterval: 0,
    revalidateOnFocus: false,
    ...swrOptions,
  }

  const authSession = useAuth()
  const fullPath = API_URL + path + (params ? '?' + new URLSearchParams(params).toString() : '')

  const { data, isLoading, error, mutate } = useSWR<ResponseData, ErrorType>(
    skip ? null : { url: fullPath, token: authSession?.token },
    fetcher<ResponseData>,
    swrConfig,
  )

  return { data, error, refetch: mutate, loading: isLoading }
}
